import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from 'constants/db.constants';
import API from '../../services/api.service';
import { Site } from 'constants/sites.constants';
import { convertKeysToCamelCase, getFormattedDate } from '../../utils/global.util';
import _ from 'lodash';
import { ErrorMessages, AppErrorCodes } from 'constants/errorMessages.constants';
import { UserRole } from 'constants/user.constants';

interface ISiteState {
  sites?: Array<Site>;
  loading?: boolean;
  error?: IStateError;
}

export interface IStateError {
  [key: string]: any;
}

const initialState: ISiteState = {
  sites: undefined,
  loading: false,
  error: undefined
};

const addCpToSite = (site: Site): Site => {
  return {
    ...site,
    activeCpCount: (site.activeCpCount || 0) + 1,
    cpCount: (site.cpCount || 0) + 1
  };
};

const updateActiveCpCountForSite = (site: Site, isActive?: boolean): Site => {
  const currentActive = site.activeCpCount || 0;
  return {
    ...site,
    activeCpCount: isActive ? currentActive + 1 : currentActive > 0 ? currentActive - 1 : 0
  };
};

const deletePatientFromSite = (site: Site, isActive: boolean): Site => {
  const patientCount = site.patientCount ?? 0;
  const activePatientCount = site.activePatientCount ?? 0;
  return {
    ...site,
    patientCount: patientCount - 1,
    activePatientCount: isActive ? activePatientCount - 1 : activePatientCount
  };
};

const updateActivePatientCountForSite = (site: Site, isActive: boolean): Site => {
  const activePatientCount = site.activePatientCount ?? 0;
  return {
    ...site,
    activePatientCount: isActive ? activePatientCount + 1 : activePatientCount - 1
  };
};

const addDoctorToSite = (site: Site): Site => ({
  ...site,
  doctorCount: (site.doctorCount || 0) + 1,
  activeDoctorCount: (site.activeDoctorCount || 0) + 1
});

const updateActiveDoctorCountForSite = (site: Site, isActive: boolean): Site => ({
  ...site,
  activeDoctorCount: isActive ? (site.activeDoctorCount || 0) + 1 : (site.activeDoctorCount || 0) - 1
});

const addAdminToSite = (site: Site): Site => ({
  ...site,
  adminCount: (site.adminCount || 0) + 1,
  activeAdminCount: (site.activeAdminCount || 0) + 1
});

const updateActiveAdminCountForSite = (site: Site, isActive: boolean): Site => ({
  ...site,
  activeAdminCount: isActive ? (site.activeAdminCount || 0) + 1 : (site.activeAdminCount || 0) - 1
});

export const getSites = createAsyncThunk('site/getSites', (_, { rejectWithValue }) =>
  API.getInstance()
    .get(API_ENDPOINTS.SITES.GET_ALL_SITES)
    .then((response) => response.data)
    .catch((err) => {
      return rejectWithValue(err);
    })
);

export const createSite = createAsyncThunk('site/createSite', async (data: any, { rejectWithValue }) => {
  try {
    const response = await API.getInstance().post(API_ENDPOINTS.SITES.CREATE_SITES, data.site);
    return {
      data: response.data,
      extra: data.extra
    };
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const updateSite = createAsyncThunk('site/updateSite', (data: any, { rejectWithValue }) =>
  API.getInstance()
    .put(`${API_ENDPOINTS.SITES.UPDATE_SITE}/${data?.id}`, data?.site)
    .then((response) => response.data)
    .catch((err) => rejectWithValue(err))
);

export const inactiveSite = createAsyncThunk('site/inactive', (data: any, { rejectWithValue }) =>
  API.getInstance()
    .post(`${API_ENDPOINTS.SITES.MAKE_SITE_INACTIVE}`, data)
    .then((response) => response.data)
    .catch((err) => rejectWithValue(err))
);

export const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    setError: (state, { payload }) => {
      if (payload === null) {
        state.error = undefined; // Reset to undefined
      } else if (payload.error) {
        state.error = { ...state.error, [payload.key]: true };
      } else {
        state.error = _.omit(state.error, payload.key);
      }
    },
    updateSitePatientCounts: (
      state,
      {
        payload
      }: {
        payload: { siteId: number; isActive: boolean; isDelete?: boolean };
      }
    ) => {
      const sites = current(state).sites || [];
      const updatedSites = sites.map((site) => {
        if (site.id === payload.siteId) {
          return payload.isDelete
            ? deletePatientFromSite(site, payload.isActive)
            : updateActivePatientCountForSite(site, payload.isActive);
        }
        return site;
      }) as Array<Site>;
      state.sites = [...updatedSites];
    },
    updateSiteCpCount: (
      state,
      {
        payload
      }: {
        payload: { sitesId: number; isActive?: boolean; isNew?: boolean };
      }
    ) => {
      const sites = current(state).sites || [];
      const updatedSites = sites.map((site) => {
        if (site.id === payload.sitesId) {
          return payload.isNew ? addCpToSite(site) : updateActiveCpCountForSite(site, payload.isActive);
        }
        return site;
      }) as Array<Site>;
      state.sites = [...updatedSites];
    },
    updateSiteUsersCount: (
      state,
      {
        payload
      }: {
        payload: { sitesIds: number[]; type: UserRole; isActive: boolean; isNew?: boolean };
      }
    ) => {
      const sites = current(state).sites || [];
      const updatedSites = sites.map((site) => {
        if (!payload.sitesIds.includes(site.id!)) return site;

        switch (payload.type) {
          case UserRole.DOCTOR:
            return payload.isNew ? addDoctorToSite(site) : updateActiveDoctorCountForSite(site, payload.isActive);
          case UserRole.ADMIN:
            return payload.isNew ? addAdminToSite(site) : updateActiveAdminCountForSite(site, payload.isActive);
          default:
            return site;
        }
      }) as Array<Site>;
      state.sites = [...updatedSites];
    }
  },
  extraReducers: {
    [getSites.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [getSites.fulfilled.type]: (state, { payload }) => {
      const convertedToCamelCaseData = _.map(payload?.data, (site) => {
        return {
          ...convertKeysToCamelCase(site),
          lastActivity: getFormattedDate(new Date(site.last_activity)),
          //  patientCount, doctorCount, siteManagerCount is a string in the API response we convert it to number because some actions expect a number
          patientCount: +site.patient_count || 0,
          doctorCount: +site.doctor_count || 0,
          adminCount: +site.admin_count || 0,
          siteManagerCount: +site.site_manager_count || 0
        };
      });
      state.sites = convertedToCamelCaseData;
      state.loading = false;
    },
    [getSites.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        errorMessage: payload?.data?.message
      };
    },
    [createSite.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [createSite.fulfilled.type]: (state, { payload }) => {
      const { data, extra } = payload;
      const site = data.data;
      const convertedSite = {
        ...convertKeysToCamelCase(site),
        ...extra,
        lastActivity: getFormattedDate(new Date(payload.data.last_activity)),
        assignedBWMR: payload.data.admin_count || '0'
      };
      state.sites?.push(convertedSite);
      state.loading = false;
    },
    [createSite.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        errorMessage: payload?.data?.message
      };
    },
    [updateSite.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [updateSite.fulfilled.type]: (state, { payload }) => {
      const sites: Site[] = [...current(state).sites!];
      const siteIndex = sites?.findIndex((site) => site.id === payload.data.id);
      if (siteIndex >= 0) {
        sites[siteIndex] = { ...sites[siteIndex], ...convertKeysToCamelCase(payload.data) };
        state.sites = sites;
      }
      state.loading = false;
    },
    [updateSite.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        errorMessage: payload?.data?.message
      };
    },
    [inactiveSite.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [inactiveSite.fulfilled.type]: (state, { payload }) => {
      const sites: Site[] = [...current(state).sites!];
      const siteIndex = sites?.findIndex((site) => site.id === payload.data.id);
      if (siteIndex >= 0) {
        sites[siteIndex] = { ...sites[siteIndex], ...payload.data };
        state.sites = sites;
      }
      state.loading = false;
    },
    [inactiveSite.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        // in case that we have app code, we will return a client error message that uses activeUsersAndPermissions, activePatients, activeCps if not, we will return a general error message
        // payload.data.data contains activeUsersAndPermissions, activePatients, activeCps
        errorMessage:
          payload?.data?.app_code === AppErrorCodes.SITE_INACTIVATION_ERROR
            ? ErrorMessages[AppErrorCodes.SITE_INACTIVATION_ERROR](payload.data.data)
            : 'Unsuccessful to inactivate site, please try again.',
        appCode: payload?.data?.app_code
      };
    }
  }
});

export const { setError, updateSitePatientCounts, updateSiteUsersCount, updateSiteCpCount } = siteSlice.actions;
export default siteSlice.reducer;
